import React, { ChangeEvent, useRef, useState } from 'react';
import { GradientText, GradientTextAction, InputLabel, Poppins, Spacer, StyledAvatar } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { getProfileCredentials } from 'src/utils/auth';
import MaterialInput from 'src/components/form/MaterialInput';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { ConfirmDeleteModal } from 'src/components/modals';
import { RestrictAccessSelect, WorkspaceTypeSelect } from 'src/components/select';
import { TabWrap } from 'src/components/TabsMenu';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { setCurrencySymbol } from 'src/utils/misc';
import { DailyDigest } from './comps';
import moment from 'moment';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import {
  useArchiveWorkspaceMutation,
  useDeleteWorkspaceMutation,
  useUnarchiveWorkspaceMutation,
  useUpsertWorkspaceMutation,
  WorkspaceQuery,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .prof-img {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 12px;
    &__input {
      display: none;
    }
  }

  form {
    .contents {
      display: grid;
      grid-gap: 50px;
      grid-template-columns: 1fr 1fr;
    }

    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 13px;
      gap: 13px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-gap: 20px;
      }
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    form {
      .contents {
        grid-gap: 10px;
        grid-template-columns: 1fr;
      }
    }
  }
`;

interface WorkspaceDetailsProps {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  primaryContact: Yup.string().email('Invalid email address').required('Required'),
  isRestricted: Yup.boolean(),
  currency: Yup.string()
    .max(3, 'Currency must be at most 3 characters long')
    .matches(/^[^\s]*$/, 'Currency must not contain spaces')
    .nullable(),
  notificationScheduleTime: Yup.string()
    .test('is-valid-time', 'Invalid time format', (value) => {
      if (!value) return true;
      return moment(value, 'HH:mm', true).isValid();
    })
    .nullable(),
});

export const WorkspaceDetails: React.FC<WorkspaceDetailsProps> = ({ workspace }) => {
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);
  const [unarchiveWarningModalOpen, setUnarchiveWarningModalOpen] = useState(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState(false);
  const { profile, features, isPlatformAdmin } = useAuth();
  const { refetchQuery } = useCache();

  const inputRef = useRef<HTMLInputElement>(null);

  const refetch = () => refetchQuery({ include: ['workspace', 'flatOrganisations', 'profile'] });

  const [update, { error }] = useUpsertWorkspaceMutation({
    onCompleted: () => refetch(),
  });

  const [archiveWorkspace, { loading: isArchiving }] = useArchiveWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: () => refetch(),
  });

  const [unarchiveWorkspace, { loading: isUnarchiving }] = useUnarchiveWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: () => refetch(),
  });

  const [deleteWorkspace, { loading: isDeleting }] = useDeleteWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: () => {
      window.location.href = '/home';
      // if (workspace.id === profile?.activeWorkspace?.id) {
      //   window.location.href = '/home';
      // } else {
      //   navigate('/home');
      //   refetchQuery({ include: ['flatOrganisations', 'profile'] });
      // }
    },
  });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: workspace.name || '',
      primaryContact: workspace.primaryContact || '',
      isRestricted: workspace.isRestricted,
      plan: workspace.plan || '',
      currency: workspace.currency || '',
      notificationScheduleTime: workspace.notificationScheduleTime,
    },
    onSubmit: (values) => {
      const currency = values.currency;

      if (currency !== workspace.currency) {
        setCurrencySymbol(currency);
        mpEvent(MPEvents.CurrencyChange, {
          currency,
        });
      }

      return update({ variables: { ...values, workspaceId: workspace.id } });
    },
    enableReinitialize: true,
  });

  const {
    values,
    handleChange,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    isValid,
    isSubmitting,
    dirty,
  } = formik;

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              archiveWorkspace();
              setArchiveWarningModalOpen(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {unarchiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              unarchiveWorkspace();
              setUnarchiveWarningModalOpen(false);
            }}
            onClose={() => setUnarchiveWarningModalOpen(false)}
            confirmText="UNARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to unarchive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {deleteWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              deleteWorkspace();
              setDeleteWarningModalOpen(false);
            }}
            onClose={() => setDeleteWarningModalOpen(false)}
            confirmText="DELETE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to delete{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        <FormikProvider value={formik}>
          <GradientText>DETAILS</GradientText>
          <Spacer $px={32} />
          <div className="prof-img">
            <StyledAvatar src={selectedImage}>{getProfileCredentials(workspace.name)}</StyledAvatar>
            <GradientTextAction onClick={() => inputRef.current?.click()} px={14} weight={500}>
              Update Photo
            </GradientTextAction>
            <input
              className="prof-img__input"
              ref={inputRef}
              type="file"
              onChange={handleImageChange}
              accept="image/*"
            />
          </div>

          <Spacer $px={32} />
          <form onSubmit={handleSubmit}>
            <div className="contents">
              <div>
                <InputLabel>NAME *</InputLabel>
                <AutoGrowingInput
                  id="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Spacer $px={20} />
                <InputLabel>PRIMARY CONTACT EMAIL ADDRESS *</InputLabel>
                <AutoGrowingInput
                  id="primaryContact"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.primaryContact}
                  type="email"
                  fullWidth
                  error={touched.primaryContact && Boolean(errors.primaryContact)}
                  helperText={touched.primaryContact && errors.primaryContact}
                />
                <Spacer $px={20} />
                <InputLabel>ORGANISATION</InputLabel>
                <MaterialInput value={profile?.activeOrganisation?.name} disabled fullWidth />
                {features['app.notifications.scheduler'] && (
                  <DailyDigest workspaceId={workspace.id} members={workspace.users} />
                )}
              </div>
              <div>
                <InputLabel
                  tooltip={`<div style="font-size: 12px;padding: 6px 0; max-width: 320px;"><b>Open:</b> (All users within the organisation have automatic access)<br /><br /><b>Restricted:</b> (Users within the organisation must be invited to join the workspace)</div>`}
                >
                  WORKSPACE RESTRICTION *
                </InputLabel>
                <RestrictAccessSelect
                  value={values.isRestricted}
                  onChange={(val) => {
                    setFieldValue('isRestricted', val.value);
                  }}
                  onBlur={handleBlur('isRestricted')}
                  error={touched.isRestricted && Boolean(errors.isRestricted)}
                  helperText={touched.isRestricted && errors.isRestricted}
                />
                <Spacer $px={20} />
                <InputLabel>ENVIRONMENT TYPE *</InputLabel>
                <WorkspaceTypeSelect
                  value={values.plan}
                  onChange={(val) => {
                    if (isPlatformAdmin) setFieldValue('plan', val.value);
                  }}
                  onBlur={handleBlur('plan')}
                  disabled={!isPlatformAdmin}
                  error={touched.plan && Boolean(errors.plan)}
                  helperText={touched.plan && errors.plan}
                />
                <Spacer $px={20} />
                <InputLabel>CURRENCY</InputLabel>
                <MaterialInput
                  id="currency"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.currency}
                  fullWidth
                  error={touched.currency && Boolean(errors.currency)}
                  helperText={touched.currency && errors.currency}
                />
              </div>
            </div>
            {error && (
              <>
                <Spacer $px={20} />
                <div className="err">{error.message || 'Something went wrong'}</div>
              </>
            )}
            <Spacer $px={50} />
            <div className="controls">
              {workspace.actionsOnThisWorkspace?.canEditSettings && (
                <Button
                  primary
                  type="submit"
                  disabled={!isValid || isSubmitting || !dirty}
                  $constWidth={220}
                  onClick={() =>
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Update',
                      tags: ['WORKSPACE'],
                    })
                  }
                >
                  UPDATE
                </Button>
              )}
              <div className="controls">
                {workspace.isArchived ? (
                  <>
                    {workspace.actionsOnThisWorkspace?.canUnarchive && (
                      <Button
                        secondary
                        type="submit"
                        disabled={isUnarchiving}
                        $constWidth={220}
                        onClick={() => setUnarchiveWarningModalOpen(true)}
                      >
                        UNARCHIVE
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {workspace.actionsOnThisWorkspace?.canArchive && (
                      <Button
                        secondary
                        type="submit"
                        disabled={isArchiving}
                        $constWidth={220}
                        onClick={() => setArchiveWarningModalOpen(true)}
                      >
                        ARCHIVE
                      </Button>
                    )}
                  </>
                )}
                {(workspace.actionsOnThisWorkspace?.canArchive || workspace.actionsOnThisWorkspace?.canUnarchive) && (
                  <Button
                    secondary
                    type="submit"
                    disabled={isDeleting || !workspace.actionsOnThisWorkspace?.canDelete}
                    $constWidth={220}
                    onClick={() => setDeleteWarningModalOpen(true)}
                  >
                    DELETE
                  </Button>
                )}
              </div>
            </div>
          </form>
        </FormikProvider>
      </Div>
    </>
  );
};
